/**
 * YT Collection Products – Specs Drawer
 * Custom element that renders product specs in a slide-in drawer.
 * Reads JSON from data-product-specs on trigger buttons.
 */

/* Global delegated click handler for yt-specs variant tabs (drawer + section) */
if (!window.__ytSpecsTabsBound) {
  window.__ytSpecsTabsBound = true;
  document.addEventListener('click', function (e) {
    var tab = e.target.closest('.yt-specs__tab');
    if (!tab) return;
    var container = tab.closest('[data-yt-specs-section]') || tab.closest('[data-active-variant]');
    if (!container) return;
    container.dataset.activeVariant = tab.dataset.variant;
    container.querySelectorAll('.yt-specs__tab').forEach(function (t) {
      t.classList.toggle('is-active', t.dataset.variant === tab.dataset.variant);
    });
  });
}

if (!customElements.get('yt-specs-drawer')) {
  class YtSpecsDrawer extends HTMLElement {
    constructor() {
      super();
      this.overlay = this.querySelector('.yt-specs-drawer__overlay');
      this.panel = this.querySelector('.yt-specs-drawer__panel');
      this.closeBtn = this.querySelector('.yt-specs-drawer__close');
      this.titleEl = this.querySelector('.yt-specs-drawer__title');
      this.imgEl = this.querySelector('.yt-specs-drawer__product-img');
      this.bodyEl = this.querySelector('.yt-specs-drawer__body');
      this.activeElement = null;

      this.categoryImages = {};
      try {
        this.categoryImages = JSON.parse(this.dataset.categoryImages || '{}');
      } catch (e) { /* ignore parse errors */ }

      this.showImage = this.dataset.showImage === 'true';

      this.closeBtn.addEventListener('click', () => this.close());
      this.overlay.addEventListener('click', () => this.close());
      this.handleKeyup = this.handleKeyup.bind(this);
    }

    connectedCallback() {
      // Dedupe: replace any existing body-level drawer so live Customizer
      // re-renders pick up updated settings (inline CSS vars, attributes).
      var existing = document.body.querySelector('yt-specs-drawer');
      if (existing && existing !== this) {
        existing.remove();
      }

      // Portal drawer to <body> so position:fixed escapes any
      // transformed/filtered ancestor that would scope it.
      if (this.parentElement !== document.body) {
        document.body.appendChild(this);
      }

      // Bind a document-level listener so triggers work regardless of which
      // section they live in (collection carousel, configurator, etc.)
      if (this._onDocClick) document.removeEventListener('click', this._onDocClick);
      this._onDocClick = (e) => {
        var trigger = e.target.closest('[data-specs-trigger]');
        if (!trigger) return;
        e.preventDefault();
        this.open(trigger);
      };
      document.addEventListener('click', this._onDocClick);
    }

    disconnectedCallback() {
      if (this._onDocClick) {
        document.removeEventListener('click', this._onDocClick);
        this._onDocClick = null;
      }
    }

    open(trigger) {
      this.activeElement = trigger;

      var title = trigger.dataset.productTitle || '';
      var imageSrc = trigger.dataset.productImage || '';
      var specsRaw = trigger.dataset.productSpecs || '[]';

      var specs;
      try {
        specs = JSON.parse(specsRaw);
      } catch (e) {
        specs = [];
      }

      this.titleEl.textContent = title;

      if (this.showImage && imageSrc) {
        this.imgEl.src = imageSrc;
        this.imgEl.alt = title;
        this.imgEl.hidden = false;
      } else {
        this.imgEl.hidden = true;
      }

      this.bodyEl.innerHTML = this.renderSpecs(specs);

      this.classList.add('is-open');
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add('overflow-hidden');
      document.addEventListener('keyup', this.handleKeyup);

      if (typeof trapFocus === 'function') {
        trapFocus(this.panel, this.closeBtn);
      }
    }

    close() {
      this.classList.remove('is-open');
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keyup', this.handleKeyup);

      /* Blur whatever is focused so the originating card's :focus-within
         state clears (otherwise the hover-bar buttons stay visible). */
      var trigger = this.activeElement;
      this.activeElement = null;
      if (document.activeElement && document.activeElement !== document.body) {
        document.activeElement.blur();
      }
      if (trigger && typeof trigger.blur === 'function') {
        trigger.blur();
      }
    }

    handleKeyup(e) {
      if (e.key === 'Escape') this.close();
    }

    collectVariantKeys(specs) {
      var keys = [];
      (specs || []).forEach((cat) => {
        (cat.rows || []).forEach((row) => {
          if (row.values && typeof row.values === 'object') {
            Object.keys(row.values).forEach((k) => {
              if (keys.indexOf(k) === -1) keys.push(k);
            });
          }
        });
      });
      return keys;
    }

    renderSpecs(specs) {
      if (!Array.isArray(specs) || specs.length === 0) {
        return '<p>No specs available.</p>';
      }

      var variantKeys = this.collectVariantKeys(specs);
      var defaultVariant = variantKeys.indexOf('29') !== -1 ? '29' : variantKeys[0];
      this.bodyEl.dataset.activeVariant = defaultVariant || '';

      var tabsHtml = '';
      if (variantKeys.length > 0) {
        var labels = { '29': '29"', 'mx': 'MX', '27': '27.5"' };
        tabsHtml = '<div class="yt-specs__tabs" role="tablist">' +
          variantKeys.map((k) => {
            var label = labels[k] || k.toUpperCase();
            var active = k === defaultVariant ? ' is-active' : '';
            return '<button type="button" class="yt-specs__tab' + active + '" data-variant="' + this.escapeAttr(k) + '" role="tab">' + this.escapeHtml(label) + '</button>';
          }).join('') +
        '</div>';
      }

      var categoriesHtml = specs.map((cat) => {
        var iconHtml = '';
        var catName = cat.category || '';
        var imgSrc = this.categoryImages[catName];
        if (imgSrc) {
          iconHtml = '<img class="yt-specs-category__icon" src="' + this.escapeAttr(imgSrc) + '" alt="' + this.escapeAttr(catName) + '" loading="lazy">';
        }

        var rowsHtml = (cat.rows || []).map((row) => this.renderRow(row)).join('');

        return (
          '<div class="yt-specs-category">' +
            '<div class="yt-specs-category__header">' +
              iconHtml +
              '<h3 class="yt-specs-category__name">' + this.escapeHtml(catName) + '</h3>' +
            '</div>' +
            '<div class="yt-specs-category__rows">' + rowsHtml + '</div>' +
          '</div>'
        );
      }).join('');

      return tabsHtml + categoriesHtml;
    }

    renderRowValue(row) {
      if (row.values && typeof row.values === 'object') {
        return Object.keys(row.values).map((k) => {
          return '<span class="yt-specs-row__value-variant" data-variant="' + this.escapeAttr(k) + '">' + this.escapeHtml(row.values[k]) + '</span>';
        }).join('');
      }
      return this.escapeHtml(row.value || '');
    }

    renderRow(row) {
      var label = this.escapeHtml(row.label || '');
      var valueHtml = this.renderRowValue(row);
      var details = row.details || '';

      if (!details) {
        return (
          '<div class="yt-specs-row yt-specs-row--simple">' +
            '<span class="yt-specs-row__label">' + label + '</span>' +
            '<span class="yt-specs-row__value">' + valueHtml + '</span>' +
          '</div>'
        );
      }

      return (
        '<div class="yt-specs-row">' +
          '<details>' +
            '<summary>' +
              '<span class="yt-specs-row__label">' + label + '</span>' +
              '<span class="yt-specs-row__value">' + valueHtml + '</span>' +
              '<span class="yt-specs-row__toggle" aria-hidden="true">+</span>' +
            '</summary>' +
            '<div class="yt-specs-row__details">' + this.escapeHtml(details) + '</div>' +
          '</details>' +
        '</div>'
      );
    }

    escapeHtml(str) {
      var div = document.createElement('div');
      div.textContent = str;
      return div.innerHTML;
    }

    escapeAttr(str) {
      return str.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
    }
  }

  customElements.define('yt-specs-drawer', YtSpecsDrawer);
}
